import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Remove from '@mui/icons-material/Remove';
import { Field } from 'redux-form';
import { useDailyRestrictionSelectors } from '../../../components/Criteria/hooks';
import CapSegmentRemainder from '../OfferActions/CapSegmentRemainder';
import CurrentCapAmountByDomains from '../OfferActions/CurrentCapAmountByDomains';
import { isPrePingBasedLogic } from '../OfferActions/OfferActionsHelpers';
import VariationStatusChip from '../../Sequences/SequenceDetails/components/VariationStatusChip';
import {
  getIsActiveTrafficLight,
  RenderNumberPicker,
  renderComboBox,
  renderDropdownList,
  renderField,
  renderMultiSelect,
  renderOfferActionDropdown,
} from '../../../components/FormFields/reduxFormRenderers';

const LABEL_SX = { display: 'block', mb: 0.5 };

/** redux-form adapter for a bare checkbox; components/FormFields has no shared one. */
const CheckboxInput = ({ input, disabled, id }) => (
  <Checkbox
    id={id}
    size='small'
    checked={Boolean(input.value)}
    disabled={disabled}
    onChange={(event) => input.onChange(event.target.checked)}
  />
);

// These three render a field, not a column. Each caller wraps the result in whatever grid it
// uses, so the same composite works in a Bootstrap `.row` (O6-O11) and in an MUI Grid (O5).
export const renderActionSelect = () => (
  <Field name='offerActionType' component={renderOfferActionDropdown} type='select' label='Offer Action Type' />
);

export const renderActionTypeDropdown = ({ handleOfferActionChange, fieldName, offerAction, data }) => (
  <>
    <FormLabel sx={LABEL_SX}>Action Type</FormLabel>
    <Field
      name={fieldName}
      component={renderDropdownList}
      onChange={(value) => handleOfferActionChange(offerAction, value)}
      type='select'
      data={data}
    />
  </>
);

// The checkbox input itself. For PRE-PING-BASED it is shown checked, locked, and — importantly —
// the form value is actually set to true, so what gets submitted matches what is displayed.
// (zuul-backend and whatif-server both enforce the same rule; this keeps the payload honest.)
const SuppressionCheckboxInput = ({ input, isPrePingBased, id }) => {
  useEffect(() => {
    if (isPrePingBased && input.value !== true) {
      input.onChange(true);
    }
  }, [isPrePingBased, input.value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Checkbox
      id={id}
      size='small'
      checked={isPrePingBased ? true : !!input.value}
      disabled={isPrePingBased}
      onChange={(event) => input.onChange(event.target.checked)}
    />
  );
};

// "Suppress Offer if action criteria fail". Normally a free checkbox, but a PRE-PING-BASED
// linkout waterfall builds its creative from the winning pre-ping — with no winner there is
// nothing to render — so suppression is always on for those. Bound to the action-name field so
// it can tell which action is selected; zuul-backend enforces the same rule on save.
const renderSuppressionCheckbox = ({ input, entityName, actionData, checkboxName }) => {
  const selectedName = typeof input.value === 'object' ? input.value?.name : input.value;
  const selectedAction = (actionData || []).find((a) => a?.name === selectedName);
  // Suppression is forced for two kinds of action: a PRE-PING-BASED waterfall, and a CPC-PP
  // linkout (its pre-ping must run before render to show and price the clicked ad). Same rule
  // in zuul-backend on save and in whatif-server regardless of the stored value.
  const isPrePingBasedWaterfall = isPrePingBasedLogic(selectedAction?.waterfallLogic);
  const isCpcPrePingLinkout = selectedAction?.payoutTypeName === 'CPC-PP';
  const isPrePingBased = isPrePingBasedWaterfall || isCpcPrePingLinkout;
  const forcedReason = isPrePingBasedWaterfall
    ? `Action is ${selectedAction.waterfallLogic === 'PRE_PING_BASED_CTR' ? 'PRE-PING-BASED-CTR' : 'PRE-PING-BASED'}`
    : 'Action is a CPC-PP linkout';

  return (
    <div>
      {/* The label carried `display: 'initial '` — a trailing space made it invalid, so the
          checkbox had been sitting on its own line ever since. */}
      <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
        <FormLabel htmlFor={checkboxName}>Suppress {entityName} if action criteria fail</FormLabel>
        <Field
          name={checkboxName}
          id={checkboxName}
          component={SuppressionCheckboxInput}
          isPrePingBased={isPrePingBased}
        />
      </Stack>
      {isPrePingBased && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InfoOutlined fontSize='inherit' />
          {forcedReason} — this checkbox is checked automatically.
        </Typography>
      )}
    </div>
  );
};

export const renderSelectedOfferAction = ({
  actionStatus,
  entityName,
  handleOfferActionNameChange,
  offerAction,
  actionType,
  data,
  disabled,
  shouldShowSuppression,
}) => {
  return (
    <>
      <Stack direction='row' spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
        <FormLabel htmlFor={`${offerAction}.name`}>Action Name</FormLabel>
        {getIsActiveTrafficLight(actionStatus)}
      </Stack>
      <Field
        name={`${offerAction}.name`}
        id={`${offerAction}.name`}
        component={renderComboBox}
        onChange={(selectedOfferAction) => handleOfferActionNameChange(selectedOfferAction, offerAction)}
        type='select'
        data={data}
        textField='name'
        busy={disabled}
      />
      {shouldShowSuppression !== false && (
        <>
          <Field
            name={`${offerAction}.name`}
            component={renderSuppressionCheckbox}
            entityName={entityName}
            actionData={data}
            checkboxName={`${offerAction}.shouldShowCriteria`}
          />
          {actionType === 'POST_WATERFALL' && (
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <FormLabel htmlFor={`${offerAction}.isPreprocessed`}>Preprocess</FormLabel>
              <Field
                name={`${offerAction}.isPreprocessed`}
                id={`${offerAction}.isPreprocessed`}
                component={CheckboxInput}
                type='checkbox'
              />
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export const handleChangeQuestionType = (fields, event) => {
  if (event.target.value === 'Yes/No') {
    switch (fields.length) {
      case 0:
        fields.push({});
        fields.push({});
        break;
      case 1:
        fields.push({});
        break;
      case 2:
        break;
      default:
        //Remove extra
        for (let i = 2; i < fields.length; i++) {
          fields.remove(2);
        }
        break;
    }
  } else if (event.target.value === 'Multi-option') {
    switch (fields.length) {
      case 0:
        fields.push({});
        break;
      default:
        break;
    }
  } else if (event.target.value === 'Multi-select') {
    switch (fields.length) {
      case 0:
        fields.push({});
        fields.push({});
        break;
      default:
        break;
    }
  } else {
    for (let i = 0; i < fields.length; i++) {
      fields.remove(0);
    }
  }
};

export const renderKVEditor = ({ fields, touch }) => {
  const keyColumnSize = 3;
  const addMetadataRow = (e) => {
    e.preventDefault();
    fields.push({ key: '', value: '' });
    touch(fields.name);
  };
  const removeMetadataRow = (idx) => (e) => {
    e.preventDefault();
    fields.remove(idx);
    touch(fields.name);
  };

  return (
    <div>
      <FormLabel sx={LABEL_SX}>Metadata</FormLabel>
      {/* The add/remove controls were icon-only buttons with an aria-hidden glyph and no
          accessible name at all. */}
      <Grid container spacing={1} sx={{ alignItems: 'center' }}>
        <Grid size='auto'>
          <IconButton aria-label='Add metadata row' onClick={addMetadataRow}>
            <Add />
          </IconButton>
        </Grid>
        <Grid size={keyColumnSize}>
          <Typography variant='subtitle2'>Key</Typography>
        </Grid>
        <Grid size='grow'>
          <Typography variant='subtitle2'>Value</Typography>
        </Grid>
      </Grid>
      {fields.map((tuple, idx) => (
        <Grid container spacing={1} key={idx} sx={{ alignItems: 'center', mt: 0.5 }}>
          <Grid size='auto'>
            <IconButton aria-label={`Remove metadata row ${idx + 1}`} onClick={removeMetadataRow(idx)}>
              <Remove />
            </IconButton>
          </Grid>
          <Grid size={keyColumnSize}>
            <Field name={`${tuple}.key`} type='text' component={renderField} />
          </Grid>
          <Grid size='grow'>
            <Field name={`${tuple}.value`} type='text' component={renderField} />
          </Grid>
        </Grid>
      ))}
    </div>
  );
};

function abbreviateText(text) {
  const abbreviationMap = {
    Question: 'Q',
    Banner: 'B',
    Form: 'F',
    'Offer Block': 'OB',
    'Offer Wall': 'OW',
    Iframe: 'I',
    Javascript: 'JS',
    'Dynamic Offer Section': 'DOS',
    // ZET-4795: hidden wrapper offer for a linkout-family offer action
    Destination: 'D',
  };

  return abbreviationMap[text] || '?';
}

export const renderOffersSequenceNode = (offer, i, dbOrderBy, offerVariations = null) => {
  // Get variation counts for primary offers
  const getOVCounts = () => {
    if (!offer?.isPrimaryVariant || !offerVariations?.length) return null;
    const active = offerVariations.filter((v) => v.isActive).length;
    const inactive = offerVariations.filter((v) => !v.isActive).length;
    return { active, inactive };
  };
  const ovCounts = getOVCounts();

  return !offer.isActive && offer.isHiddenPlaceholder ? (
    <>
      <div
        className={`offerName topHalfDiv ${offer.isActive ? 'active' : 'inactive'}`}
        id={`offersSequence-${offer.id}-${i}`}
        name={`offersSequence-${offer.id}-${i}`}
      >
        <p>{`${offer.hiddenOfferName}`}</p>
      </div>
      <div className='bottomHalfDiv' />
    </>
  ) : (
    <>
      <div
        className={`offerName topHalfDiv ${offer.isActive ? 'active' : 'inactive'}`}
        id={`offersSequence-${offer.id}-${i}`}
        name={`offersSequence-${offer.id}-${i}`}
      >
        <span title={offer ? offer.name : null}>
          {offer ? offer?.name?.substring(0, 30) : null}
          {offer && offer.name.length > 30 ? '...' : ''}
        </span>
      </div>
      <div className='bottomHalfDiv'>
        <span>{dbOrderBy}</span>
        <span className='right-section'>
          {ovCounts && (
            <span className='ov-status-indicators'>
              {ovCounts.inactive > 0 && (
                <VariationStatusChip isActive={false} title={`${ovCounts.inactive} paused variant(s)`}>
                  {ovCounts.inactive}
                </VariationStatusChip>
              )}
              {ovCounts.active > 0 && (
                <VariationStatusChip isActive title={`${ovCounts.active} live variant(s)`}>
                  {ovCounts.active}
                </VariationStatusChip>
              )}
            </span>
          )}
          <span className='offerTypeName'>
            {abbreviateText(offer?.offerType?.name ? offer.offerType.name : offer.offerType)}
          </span>
        </span>
      </div>
    </>
  );
};

export const renderOrphanedOffers = (orphanedOffers, handleOrphanedOfferClick) => {
  return orphanedOffers.map((offerSequence, i) => {
    return (
      <span key={offerSequence.id}>
        <a href={`#offersSequence-${offerSequence.id}`} onClick={handleOrphanedOfferClick} data-id={offerSequence.id}>
          {offerSequence.name}
        </a>
        {orphanedOffers.length > 1 && i < orphanedOffers.length - 1 ? ' | ' : ''}
      </span>
    );
  });
};

export const RenderCapSegmentConfiguration = ({
  fields,
  domains,
  capAmount,
  capSegmentConfiguration,
  offerCriteriaId,
}) => {
  const dailyRestrictionSelectors = useDailyRestrictionSelectors();
  const selectedDomains = dailyRestrictionSelectors?.capSegmentConfiguration?.capSegments.flatMap(
    (segment) => segment?.domains || []
  );

  const updateCapSegmentRemainder = (newRemainder) => {
    if (newRemainder !== capSegmentConfiguration.capSegmentRemainder) {
      capSegmentConfiguration.capSegmentRemainder = newRemainder;
    }
  };

  const handleSegmentRemove = (index) => {
    fields.remove(index);
  };

  return (
    <div>
      {fields.map((capSegment, index) => (
        // The row keeps the default stretch so the three labels line up; only the remove button
        // drops to the bottom, which is what the <br /> above it used to do.
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid size='auto' sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <IconButton
              color='error'
              aria-label={`Remove cap segment ${index + 1}`}
              onClick={() => handleSegmentRemove(index)}
            >
              <Close />
            </IconButton>
          </Grid>
          <Grid size='grow'>
            <FormLabel sx={LABEL_SX}>Domains</FormLabel>
            <Field
              name={`${capSegment}.domains`}
              component={renderMultiSelect}
              data={domains ? domains.filter((domain) => !selectedDomains.includes(domain)) : []}
            />
          </Grid>
          <Grid size='grow'>
            <FormLabel sx={LABEL_SX}>Segment Amount</FormLabel>
            <Field name={`${capSegment}.segmentAmount`} component={RenderNumberPicker} defaultValue={0} />
          </Grid>
          <Grid size='grow'>
            <CurrentCapAmountByDomains domains={fields.get(index)?.domains} id={offerCriteriaId} />
          </Grid>
        </Grid>
      ))}
      {/* The empty cells keep the remainder under the Segment Amount column, which is what the
          spacer <Col />s did. */}
      {capSegmentConfiguration?.capSegments?.length > 0 && (
        <Grid container spacing={2}>
          <Grid size='auto' sx={{ visibility: 'hidden' }}>
            <IconButton aria-hidden='true' tabIndex={-1}>
              <Close />
            </IconButton>
          </Grid>
          <Grid size='grow' />
          <Grid size='grow'>
            <FormLabel sx={LABEL_SX}>Segment Remainder</FormLabel>
          </Grid>
          <Grid size='grow' />
        </Grid>
      )}
      <Grid container spacing={2} sx={{ alignItems: 'flex-end' }}>
        <Grid size='auto' sx={{ visibility: 'hidden' }}>
          <IconButton aria-hidden='true' tabIndex={-1}>
            <Close />
          </IconButton>
        </Grid>
        <Grid size='grow' />
        <Grid size='grow'>
          <CapSegmentRemainder
            capAmount={capAmount}
            capSegments={capSegmentConfiguration?.capSegments}
            capSegmentRemainder={capSegmentConfiguration?.capSegmentRemainder}
            updateCapSegmentRemainder={updateCapSegmentRemainder}
          />
        </Grid>
        <Grid size='grow'>
          <Button
            variant='contained'
            color='success'
            onClick={() =>
              fields.push({
                domains: [],
                segmentAmount: 0,
              })
            }
          >
            Add Cap Segments
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};
